/**
 * WS-C §4 — where an interrupted visitor goes after signing in.
 *
 * ⛔ THE SHELL'S OWN KEY. Never `cp_return_to` — that belongs to the old console, and the two SPAs
 * share an origin (D-9′). `sessionStorage` is origin-scoped exactly like `localStorage`, so a shared
 * key is two writers to one slot; the failure is milder than the refresh-token race (a wrong
 * destination rather than a dead session) but it is the same rule.
 */
import { REFUSAL_PATHS } from './refusal-paths';

export const RETURN_TO_KEY = 'zion.cpadmin.return_to';

/**
 * ⚠️ TRANSITIONAL, ONE RELEASE ONLY (WS-8 E-2).
 *
 * A tab that started a login while the console was served at `/cp-admin/` stashed a
 * `/cp-admin/`-prefixed browser path. R-3 keeps both `?ui=` callback keys so that login LANDS;
 * without the strip below it lands on the not-found pane, which defeats the guarantee R-3
 * exists to provide. `sessionStorage` is per-tab, so every such value is gone the moment those
 * tabs close.
 *
 * ⛔ REMOVE IN THE RELEASE AFTER the one that ships the root cutover.
 */
const LEGACY_BASE = '/cp-admin';

/**
 * Is this a safe, in-app, browser-absolute path?
 *
 * ⛔ VALIDATED ON READ, even though it is same-origin `sessionStorage` we wrote ourselves. Reject the
 * SHAPE, not the instance:
 *   · `//evil.example/x` is a **protocol-relative URL** — anything that later hands it to a browser
 *     API resolves it OFF-ORIGIN. It starts with `/`, so a naive "is it absolute" check passes it.
 *   · `https://evil.example` and `\\evil.example` are the same class through different encodings.
 *
 * ⛔ THE SECURITY PROPERTY IS UNCHANGED BY THE ROOT CUTOVER, AND THE ORDER OF THESE THREE CHECKS
 * IS LOAD-BEARING. There used to be a fourth line requiring the value to sit inside `/cp-admin`.
 * At the origin root every same-origin absolute path is in-app, so that containment check is
 * subsumed by the first line — but `//evil.example/x` and `\\evil.example` are still rejected, by
 * the second and third, and they are still checked BEFORE anything else because they are the two
 * that look absolute-and-safe to a naive reader.
 *
 * ⚠️ The threat model is not "an attacker writes our sessionStorage" — if they can do that they have
 * script execution already. It is that a stored value outlives the code that wrote it: a stale entry
 * from an older build, a hand-edited devtools value, or a future writer that forgets the invariant.
 * That is now the WHOLE justification for validating on read, and the cutover makes it sharper
 * rather than weaker — see the legacy-prefix strip in `takeReturnTo`, which exists precisely
 * because a previous deploy's values are still in tabs.
 */
function isSafeReturnPath(value: string): boolean {
  if (!value.startsWith('/')) return false;
  // Protocol-relative. Checked before anything else, because it is the one that looks safe.
  if (value.startsWith('//')) return false;
  // A backslash is treated as a slash by several URL parsers.
  if (value.includes('\\')) return false;
  return true;
}

/**
 * Stash where the visitor was trying to go, as a BROWSER-ABSOLUTE path.
 *
 * ⛔ `window.location`, NOT `useLocation()`. The two agree at the origin root, but they are not the
 * same thing and the difference is what this rule protects: under any non-empty `basename` the
 * router STRIPS it, so stashing a router pathname and later handing it to anything browser-shaped
 * produces the wrong destination. Storing the browser path keeps ONE representation in storage.
 *
 * ⚠️ `pathname + search`, not just `pathname`. The destination this exists to preserve can carry
 * query state, and a pathname-only stash returns the operator to a page with its parameters dropped
 * — which for a filtered view is indistinguishable from the filter having been reset.
 */
export function stashReturnTo(location: Pick<Location, 'pathname' | 'search'> = window.location): void {
  const value = `${location.pathname}${location.search}`;
  // Never stash the login page itself: after signing in that is a redirect to where you already are.
  if (value.startsWith('/login')) return;
  /*
   * ⛔ NOR A REFUSAL PAGE (#2496). A person refused at the OAuth callback lands on
   * `/sign-in-unavailable`, and that page is unauthenticated, so the splat below stashes it on the
   * way past. Once an administrator enables the account, the NEXT sign-in in that same tab reads
   * the stash and returns the operator to `/sign-in-unavailable` — which, now that they hold a
   * session, is no longer a matched route and renders the not-found pane. A destination that
   * exists before sign-in and not after is exactly what this stash must not hold.
   *
   * ⛔ EXACT MATCH OR `path?`, NOT `startsWith` — the opposite choice from `/login` above, and
   * deliberate. `startsWith('/sign-in-unavailable')` would also swallow a future
   * `/sign-in-unavailable-appeal`, and a silently-dropped deep link is the kind of defect that
   * only shows up as "it sometimes forgets where I was". `/login`'s existing form is left alone:
   * it predates this and has its own callers.
   */
  if (REFUSAL_PATHS.some((path) => value === path || value.startsWith(`${path}?`))) return;
  if (!isSafeReturnPath(value)) return;
  try {
    sessionStorage.setItem(RETURN_TO_KEY, value);
  } catch {
    // Private-mode storage throws. Losing the deep link is a downgrade to the landing page, which is
    // exactly what happens when there is no stash at all — so there is nothing to report.
  }
}

/**
 * Consume the stash EXACTLY ONCE, returning a ROUTER-RELATIVE path or `null`.
 *
 * Callers do `navigate(takeReturnTo() ?? landingPath(landing), { replace: true })`, and at the
 * origin root both arms of that `??` are the same coordinate system by construction.
 *
 * ⛔ Removed on read whether or not it is used. A stash that survives its own consumption sends the
 * NEXT sign-in somewhere the operator did not ask to go, and that one is genuinely confusing because
 * it works correctly the first time.
 */
export function takeReturnTo(): string | null {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    return null;
  }
  if (stored === null) return null;
  // ⛔ Rejected BEFORE any rewriting: a hostile stored value must never reach the strip below.
  if (!isSafeReturnPath(stored)) return null;
  // ⚠️ TRANSITIONAL — see LEGACY_BASE. Prefix-WITH-SEPARATOR, never `startsWith('/cp-admin')`:
  // the latter would also maul `/cp-admin-evil/x` and `/cp-adminology`.
  if (stored === LEGACY_BASE) return '/';
  if (stored.startsWith(`${LEGACY_BASE}/`)) return stored.slice(LEGACY_BASE.length);
  return stored;
}

/*
 * The Grafana return (plan 2026-10-07-grafana-host-and-login-redirect, decision 5).
 *
 * cp-auth-api answers a signed-out page load of `/grafana/...` with a 302 to
 * `/login?next=<the Grafana path>`. Grafana is a different program behind the same origin, so the
 * console cannot return there through the router: a router navigation to `/grafana/...` lands on
 * the console's catch-all. The functions below carry `next` through sign-in in the return-to stash,
 * and `CpAdminApp`'s `GrafanaReturnGate` sends the browser back with one full page load.
 */

/** The longest Grafana path the console accepts in `next`. cp-auth-api carries the same limit. */
export const GRAFANA_RETURN_PATH_MAX = 2048;

/**
 * The `sessionStorage` key of the loop guard: the time, in epoch milliseconds, of the last full
 * page load to `/grafana` that the console made.
 */
export const GRAFANA_RETURN_GUARD_KEY = 'zion.cpadmin.grafana_return_at';

/** The loop guard's window. A second Grafana return inside it is refused. */
export const GRAFANA_RETURN_GUARD_MS = 30_000;

/**
 * The `/grafana` shape: `/grafana`, or `/grafana/` and more, then one optional query, over the RFC
 * 3986 `pchar` set plus `/`. It is the rule cp-auth-api's `isGrafanaReturnPath` applies before it
 * writes `next`, so the console accepts what the server sends and nothing wider.
 */
const GRAFANA_RETURN_SHAPE = /^\/grafana(?:\/[A-Za-z0-9\-._~!$&'()*+,;=:@%/]*)?(?:\?[A-Za-z0-9\-._~!$&'()*+,;=:@%/]*)?$/;

/** A `%` that two hex digits do not follow. */
const BROKEN_PERCENT = /%(?![0-9A-Fa-f]{2})/;

/** An escape of `/`, `\`, a C0 control byte or DEL, in either case. Read in the path part only. */
const ENCODED_SEPARATOR_OR_CONTROL = /%(?:2f|5c|[01][0-9a-f]|7f)/i;

/**
 * Whether `value` is a Grafana path the console may send the browser to with a full page load.
 *
 * The value must pass `isSafeReturnPath`, then every rule of cp-auth-api's `isGrafanaReturnPath`:
 * at most `GRAFANA_RETURN_PATH_MAX` characters, the `/grafana` shape, no `//`, every `%` a
 * two-hex-digit escape, no `%2F`, `%5C`, `%00` to `%1F` or `%7F` in the path part before the first
 * `?` (either case), and no `.` or `..` path segment with `%2e` read as a dot. It decodes nothing.
 *
 * @returns true for `/grafana/explore?x=1`; false for `https://evil.example`, `//evil`,
 *   `/settings` and `/grafana\x`.
 */
export function isGrafanaReturnPath(value: string): boolean {
  if (!isSafeReturnPath(value)) return false;
  if (value.length > GRAFANA_RETURN_PATH_MAX) return false;
  if (!GRAFANA_RETURN_SHAPE.test(value)) return false;
  if (value.includes('//')) return false;
  if (BROKEN_PERCENT.test(value)) return false;
  const queryAt = value.indexOf('?');
  const path = queryAt === -1 ? value : value.slice(0, queryAt);
  if (ENCODED_SEPARATOR_OR_CONTROL.test(path)) return false;
  return path.split('/').every((segment) => {
    const dots = segment.replace(/%2e/gi, '.');
    return dots !== '.' && dots !== '..';
  });
}

/**
 * The Grafana path in a `/login` query's `next` parameter, or `null`.
 *
 * `URLSearchParams` undoes the one encoding cp-auth-api applied to the value, and the result is
 * checked as it stands, with no second decode.
 *
 * @param search a location's `search`, with or without the leading `?`.
 * @returns the `next` value when it passes `isGrafanaReturnPath`; `null` when it is absent or
 *   refused. A repeated `next` reads its first value.
 */
export function grafanaNextOf(search: string): string | null {
  const next = new URLSearchParams(search).get('next');
  return next !== null && isGrafanaReturnPath(next) ? next : null;
}

/**
 * Stash the Grafana path of `/login?next=...` as the return-to destination. `LoginPage` calls it
 * on mount. A `next` that `grafanaNextOf` refuses leaves the stash as it is.
 */
export function stashGrafanaNext(search: string = window.location.search): void {
  const next = grafanaNextOf(search);
  if (next === null) return;
  try {
    sessionStorage.setItem(RETURN_TO_KEY, next);
  } catch {
    // Private-mode storage throws. The operator lands on the console, as with no stash at all.
  }
}

/** The stash when it holds a Grafana path, without consuming it; else `null`. */
function peekGrafanaReturnTo(): string | null {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(RETURN_TO_KEY);
  } catch {
    return null;
  }
  return stored !== null && isGrafanaReturnPath(stored) ? stored : null;
}

/**
 * Where a signed-in console at `location` must send the browser by full page load, or `null`.
 *
 * 1. At a router location under `/grafana`: that path and query. A sign-in page that returned
 *    there through the router lands here.
 * 2. At `/login`: the `next` query value, else a Grafana path held in the stash. A session that
 *    already existed when cp-auth-api's 302 arrived lands here.
 * 3. Anywhere else, or when the value fails `isGrafanaReturnPath`: `null`.
 *
 * It reads the stash and writes nothing, so a render may call it.
 */
export function grafanaReturnTargetOf(location: Pick<Location, 'pathname' | 'search'>): string | null {
  const { pathname, search } = location;
  if (pathname === '/grafana' || pathname.startsWith('/grafana/')) {
    const target = `${pathname}${search}`;
    return isGrafanaReturnPath(target) ? target : null;
  }
  if (pathname === '/login') return grafanaNextOf(search) ?? peekGrafanaReturnTo();
  return null;
}

/** The constant same-origin prefix of every Grafana page load. */
const GRAFANA_PREFIX = '/grafana';

/**
 * Load the Grafana page at `target` with a full page load.
 *
 * The URL is the constant `/grafana` prefix plus the rest of a path that `isGrafanaReturnPath`
 * accepted, so no input can choose the scheme, the host or a path outside `/grafana`.
 *
 * @param target a value `grafanaReturnTargetOf` returned.
 */
export function openGrafanaPath(target: string): void {
  if (!isGrafanaReturnPath(target)) return;
  window.location.assign(GRAFANA_PREFIX + target.slice(GRAFANA_PREFIX.length));
}

/**
 * Take one Grafana return: drop a Grafana path from the stash, then apply the loop guard.
 *
 * A cookie that never reaches Grafana sends the browser from Grafana to `/login?next=...` and back
 * to this console, which would send it to Grafana again. The guard stops the second hop: it
 * refuses a return when the last one was less than `GRAFANA_RETURN_GUARD_MS` ago, so the operator
 * stays on the console landing page.
 *
 * @param now the current time in epoch milliseconds.
 * @returns true when the caller may make the full page load (the guard then records `now`);
 *   false when a return happened inside the window, or when storage throws, because a guard that
 *   cannot record cannot stop a loop.
 */
export function claimGrafanaReturn(now: number = Date.now()): boolean {
  try {
    const stored = sessionStorage.getItem(RETURN_TO_KEY);
    if (stored !== null && isGrafanaReturnPath(stored)) sessionStorage.removeItem(RETURN_TO_KEY);
    const last = Number(sessionStorage.getItem(GRAFANA_RETURN_GUARD_KEY));
    if (last > 0 && now >= last && now - last < GRAFANA_RETURN_GUARD_MS) return false;
    sessionStorage.setItem(GRAFANA_RETURN_GUARD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}
